// src/hooks/useEpoReviewModal.js
//
// Batch A (Round 3) — shared helper that lets ANY EPO grid open the single
// reusable EpoReviewModal with one line, while keeping each grid self-contained
// (no global provider, no App.js / routing changes — lowest regression risk).
//
// Usage in a grid page:
//
//   import useEpoReviewModal from "../../hooks/useEpoReviewModal";
//   const epoReview = useEpoReviewModal({
//       permissions: { canPay: true },     // optional, controls which buttons SHOW
//       onActionDone: () => reload(),       // optional, refresh the grid ONLY after a state-changing action
//   });
//   ...
//   <button onClick={() => epoReview.open(epoNum)}>View</button>
//   <tr onDoubleClick={() => epoReview.open(epoNum)}> ... </tr>
//   ...
//   {epoReview.element}   // render once at the bottom of the page
//
// Filter preservation: because this opens a MODAL (no navigation), the grid's
// own filter/sort/page state is untouched while the modal is open and after it
// closes. The grid reloads ONLY when onActionDone fires (i.e. a state-changing
// action happened inside the modal), never on a plain close.

import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EpoReviewModal from "../pages/ExpensePurchaseOrder/EpoReviewModal";

export default function useEpoReviewModal(options = {}) {
    const { permissions = {}, onActionDone } = options;
    const navigate = useNavigate();
    const [epoNumber, setEpoNumber] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [initialTab, setInitialTab] = useState("basic");

    const open = useCallback((num, opts = {}) => {
        if (num === null || num === undefined || num === "") return;
        setEpoNumber(Number(num));
        setInitialTab(opts.tab || "basic");
        setIsOpen(true);
    }, []);

    const close = useCallback(() => setIsOpen(false), []);

    const element = useMemo(
        () => (
            <EpoReviewModal
                isOpen={isOpen}
                toggle={close}
                epoNumber={epoNumber}
                initialTab={initialTab}
                permissions={permissions}
                onActionDone={onActionDone}
                onNavigate={(path) => { setIsOpen(false); navigate(path); }}
            />
        ),
        // permissions/onActionDone are stable per page; epoNumber/isOpen drive renders
        [isOpen, epoNumber, initialTab]
    );

    return { open, close, isOpen, epoNumber, element };
}
